/** 24-hour HH:MM */
export const TIME_HHMM_REGEX = /^\d{2}:\d{2}$/

export const CHECKIN_CLOSE_MODES = {
  MINUTES_BEFORE: 'minutes_before',
  FIXED_TIME: 'fixed_time'
}

export const CHECKIN_CLOSE_OFFSET_OPTIONS = [60, 90, 120]

export function parseTimeHHMM (timeStr) {
  if (!timeStr || !TIME_HHMM_REGEX.test(timeStr)) return null
  const [hh, mm] = timeStr.split(':').map(Number)
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null
  return { hours: hh, minutes: mm }
}

export function parseDateTimeLocal (dateStr, timeStr) {
  const parsed = parseTimeHHMM(timeStr)
  if (!parsed || !dateStr) return null
  const parts = String(dateStr).split('T')[0].split('-').map(Number)
  if (parts.length !== 3 || parts.some(n => !Number.isFinite(n))) return null
  const [y, m, d] = parts
  return new Date(y, m - 1, d, parsed.hours, parsed.minutes, 0, 0)
}

export function inferCheckInCloseMode (record: Record<string, any> = {}) {
  if (record.checkInCloseMode === CHECKIN_CLOSE_MODES.FIXED_TIME) {
    return CHECKIN_CLOSE_MODES.FIXED_TIME
  }
  if (record.checkInCloseMode === CHECKIN_CLOSE_MODES.MINUTES_BEFORE) {
    return CHECKIN_CLOSE_MODES.MINUTES_BEFORE
  }
  if (parseTimeHHMM(record.checkInCloseTime)) {
    return CHECKIN_CLOSE_MODES.FIXED_TIME
  }
  return CHECKIN_CLOSE_MODES.MINUTES_BEFORE
}

/**
 * Resolve check-in close instant for a game day.
 * @returns {Date|null}
 */
export function resolveCheckInCloseAt ({
  dateStr,
  startTime,
  checkInCloseMode,
  checkInCloseTime,
  checkInCloseOffsetMinutes
}: {
  dateStr?: any
  startTime?: any
  checkInCloseMode?: any
  checkInCloseTime?: any
  checkInCloseOffsetMinutes?: any
} = {}) {
  const mode = checkInCloseMode || inferCheckInCloseMode({
    checkInCloseMode,
    checkInCloseTime,
    checkInCloseOffsetMinutes
  })

  if (mode === CHECKIN_CLOSE_MODES.FIXED_TIME) {
    const fixed = parseDateTimeLocal(dateStr, checkInCloseTime)
    if (fixed) return fixed
  }

  const offset = Number(checkInCloseOffsetMinutes)
  if (Number.isFinite(offset) && offset >= 0 && startTime) {
    const startAt = parseDateTimeLocal(dateStr, startTime)
    if (startAt) {
      return new Date(startAt.getTime() - offset * 60 * 1000)
    }
  }

  return parseDateTimeLocal(dateStr, '18:00')
}

export function formatClockLabel (hour24, minute) {
  const ampm = hour24 >= 12 ? 'PM' : 'AM'
  const displayHour = hour24 > 12 ? hour24 - 12 : (hour24 === 0 ? 12 : hour24)
  return `${displayHour}:${String(minute).padStart(2, '0')} ${ampm}`
}

export function formatCheckInCloseSummary (record: Record<string, any> = {}) {
  const mode = inferCheckInCloseMode(record)
  if (mode === CHECKIN_CLOSE_MODES.FIXED_TIME && parseTimeHHMM(record.checkInCloseTime)) {
    const parsed = parseTimeHHMM(record.checkInCloseTime)!
    return `closes ${formatClockLabel(parsed.hours, parsed.minutes)}`
  }
  const mins = Number(record.checkInCloseOffsetMinutes)
  const label = Number.isFinite(mins) ? mins : 60
  return `closes ${label}m before puck drop`
}

export function buildCheckInCloseFields ({
  mode,
  checkInCloseOffsetMinutes,
  checkInCloseTime
}: { mode?: any; checkInCloseOffsetMinutes?: any; checkInCloseTime?: any } = {}) {
  const normalizedMode =
    mode === CHECKIN_CLOSE_MODES.FIXED_TIME
      ? CHECKIN_CLOSE_MODES.FIXED_TIME
      : CHECKIN_CLOSE_MODES.MINUTES_BEFORE

  if (normalizedMode === CHECKIN_CLOSE_MODES.FIXED_TIME) {
    const parsed = parseTimeHHMM(checkInCloseTime)
    if (!parsed) {
      return { valid: false, error: 'Enter a valid close time (24h, e.g. 17:00)' }
    }
    const hh = String(parsed.hours).padStart(2, '0')
    const mm = String(parsed.minutes).padStart(2, '0')
    return {
      valid: true,
      fields: {
        checkInCloseMode: CHECKIN_CLOSE_MODES.FIXED_TIME,
        checkInCloseTime: `${hh}:${mm}`,
        checkInCloseOffsetMinutes: null
      }
    }
  }

  const offset = Number(checkInCloseOffsetMinutes)
  if (!CHECKIN_CLOSE_OFFSET_OPTIONS.includes(offset)) {
    return { valid: false, error: 'Choose 60, 90, or 120 minutes before puck drop' }
  }

  return {
    valid: true,
    fields: {
      checkInCloseMode: CHECKIN_CLOSE_MODES.MINUTES_BEFORE,
      checkInCloseTime: null,
      checkInCloseOffsetMinutes: offset
    }
  }
}

export function scheduleCheckInFieldsFromRecord (schedule: Record<string, any> = {}) {
  const mode = inferCheckInCloseMode(schedule)
  return {
    checkInOpenTime: schedule.checkInOpenTime || '08:00',
    checkInCloseMode: mode,
    checkInCloseOffsetMinutes:
      mode === CHECKIN_CLOSE_MODES.MINUTES_BEFORE
        ? Number(schedule.checkInCloseOffsetMinutes || 60)
        : 60,
    checkInCloseTime:
      mode === CHECKIN_CLOSE_MODES.FIXED_TIME
        ? (schedule.checkInCloseTime || '18:00')
        : ''
  }
}
